import usePermissions from "@/hooks/use-permissions.hook.ts";
import useTimeOffHook from "@/hooks/use-time-off.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { getEmployeeTimeOffCategoryCaption } from "@/pages/employees/employee/components/employee-time-off-caption.helper.ts";
import { EmployeeTimeOffCalendar } from "@/pages/employees/employee/components/EmployeeTimeOffCalendar.tsx";
import { TimeOffHistoryDetailsModal } from "@/pages/time-off/widgets/TimeOffHistoryModal.tsx";
import { TimeOffPolicyChecksModal } from "@/pages/time-off/widgets/TimeOffPolicyChecksModal.tsx";
import {
  useEmployeeTimeOffCalendar,
  useEmployeeTimeOffCategoriesData,
} from "@/queries/employees/use-employees.query.ts";
import { useTimeOffCategories } from "@/queries/use-time-off.query.ts";
import {
  ApiEmployeeLifecycleStatus,
  ApiEmployeeTimeOffCategoryPayload,
} from "@/types/employees/employee.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import {
  ApiTimeOffCategoryType,
  ApiTimeOffPeriodTrackingTime,
} from "@/types/time-off/time-off-policy.types.ts";
import {
  Box,
  Button,
  ButtonIcon,
  ButtonLink,
  CardMini,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Title,
  toColorName,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const EmployeeTimeOff = ({ employee }: EmployeePageComponentProps) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { canEditField, canIEdit } = usePermissions();
  const { openAdjustmentBalance } = useTimeOffHook();
  const { renderShortDate } = useFormatter();
  const [payloadCategory] = useState<ApiEmployeeTimeOffCategoryPayload>({
    date_balance: new Date(),
  });
  const [calendarYear, setCalendarYear] = useState<number>(new Date().getFullYear());
  const { data: employeeTimeOffCategories, isLoading: employeeTimeOffCategoriesIsLoading } =
    useEmployeeTimeOffCategoriesData(employee.id, payloadCategory);
  const { data: employeeTimeOffCalendar, isLoading: employeeTimeOffCalendarIsLoading } =
    useEmployeeTimeOffCalendar(employee.id, calendarYear);
  const { data: timeOffCategories } = useTimeOffCategories({
    enabled: canIEdit(ApiPermission.manage_leave_policies),
  });
  const canChangeTimeOff =
    canIEdit(ApiPermission.approve_and_reject_leaves) ||
    canEditField(employee, EmployeeFieldPermission.employee_time_off);
  const canViewTimeOffHistory =
    canIEdit(ApiPermission.manage_leave_policies) ||
    canEditField(employee, EmployeeFieldPermission.employee_time_off);
  const terminationDate =
    employee.lifecycle_status === ApiEmployeeLifecycleStatus.terminated
      ? employee.date_termination
      : undefined;
  const balancesCaption = terminationDate
    ? t("time_off:balance_as_of", {
        date:
          terminationDate.getFullYear() === new Date().getFullYear()
            ? renderShortDate(terminationDate)
            : `${renderShortDate(terminationDate)} ${terminationDate.getFullYear()}`,
      })
    : undefined;

  const handlePrevYear = () => setCalendarYear((prev) => prev - 1);
  const handleNextYear = () => setCalendarYear((prev) => prev + 1);

  return (
    <Stack>
      <Stack gap="lg">
        <Stack justify={"between"} direction={"horizontal"} items={"center"} gap={"md"}>
          <Title header={t("common:balances")} caption={balancesCaption} size={"lg"} />
          <Stack direction={"horizontal"} gap={"sm"}>
            {canIEdit(ApiPermission.manage_leave_policies) &&
              (timeOffCategories?.length !== 0 ? (
                <DropdownMenu
                  trigger={
                    <Tooltip delay={false}>
                      <TooltipTrigger asChild>
                        <ButtonIcon
                          variant="secondary"
                          size="sm"
                          icon={<FontAwesomeIcon icon="fa-light fa-ellipsis" />}
                        />
                      </TooltipTrigger>
                      <TooltipContent>{t("common:more")}</TooltipContent>
                    </Tooltip>
                  }
                >
                  <DropdownMenuItem
                    leftIcon={<FontAwesomeIcon icon="fa-light fa-pen-to-square" />}
                    title={t("time_off:edit_balance")}
                    onClick={() => openAdjustmentBalance(employee)}
                  />
                  {timeOffCategories && (
                    <DropdownMenuItem
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-question" />}
                      title={t("time_off:policy_checks_title")}
                      onClick={() => {
                        openModal(TimeOffPolicyChecksModal, {
                          employee,
                          categories: timeOffCategories,
                        });
                      }}
                    />
                  )}
                </DropdownMenu>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    openAdjustmentBalance(employee);
                  }}
                >
                  {t("time_off:edit_balance")}
                </Button>
              ))}
            {canChangeTimeOff && (
              <ButtonLink
                variant="primary"
                size="sm"
                href={`/time-off/request/new?employeeId=${employee.id}`}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
              >
                {t("time_off:request_timeoff")}
              </ButtonLink>
            )}
          </Stack>
        </Stack>
        <Stack className="flex-wrap" direction={"horizontal"} gap={"md"}>
          {employeeTimeOffCategoriesIsLoading && (
            <CardMini title={t("common:loading")} emoji={"⌛"} />
          )}
          {employeeTimeOffCategories?.map((item, index) => {
            const isCurrentTimeOffCategory = employee.time_off?.category.id === item.id;
            const periodLocaleKey =
              item.period_track_type === ApiTimeOffPeriodTrackingTime.hours
                ? "plurals:plural_hours"
                : "plurals:plural_day";
            const bookedTag =
              item.balance.total_booked_amount && parseFloat(item.balance.total_booked_amount) > 0
                ? t("plurals:booked", {
                    units: t(periodLocaleKey, {
                      count: parseFloat(item.balance.total_booked_amount),
                    }),
                    count: parseFloat(item.balance.total_booked_amount),
                  })
                : "";
            const currentTimeOffTag =
              isCurrentTimeOffCategory && employee.time_off
                ? t("time_off:away_till", {
                    date: renderShortDate(employee.time_off.date_to),
                  })
                : "";

            return (
              <CardMini
                key={`time_off_${index}`}
                title={item.title}
                emoji={item.emoji}
                accentColor={toColorName(item.color)}
                tag={currentTimeOffTag || bookedTag}
                caption={getEmployeeTimeOffCategoryCaption(item, t)}
                onClick={
                  canViewTimeOffHistory && item.category_type == ApiTimeOffCategoryType.accrues_time
                    ? () => {
                        openModal(TimeOffHistoryDetailsModal, {
                          employeeId: employee.id,
                          timeOffCategory: item,
                        });
                      }
                    : undefined
                }
                to={
                  canChangeTimeOff && item.category_type != ApiTimeOffCategoryType.accrues_time
                    ? `/time-off/request/new?employeeId=${employee.id}&categoryId=${item.id}`
                    : undefined
                }
              />
            );
          })}
          <CardMini
            to={`/employees/${employee.id}/time-off-history`}
            icon={"fa-regular fa-clock-rotate-left"}
            title={t("common:history")}
            caption={t("time_off:all_balance_changes")}
          />
        </Stack>
      </Stack>
      <Stack gap={"md"}>
        <Title header={t("common:calendar")} size={"lg"} paddingTop />
        <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
          <ButtonIcon
            icon={<FontAwesomeIcon icon="fa-light fa-angle-left" />}
            variant={"secondary"}
            onClick={handlePrevYear}
          />
          <Box className={"px-2 text-center font-semibold"}>{calendarYear}</Box>
          <ButtonIcon
            icon={<FontAwesomeIcon icon="fa-light fa-angle-right" />}
            variant={"secondary"}
            onClick={handleNextYear}
          />
        </Stack>
        <EmployeeTimeOffCalendar
          data={employeeTimeOffCalendar}
          isLoading={employeeTimeOffCalendarIsLoading}
          year={calendarYear}
        />
      </Stack>
    </Stack>
  );
};
